import { useEffect, useState } from "react";
import Badge from '../../components/Badge';
import Card from "../../components/Card";
import FilterPanel from "../../components/FilterPanel";
import Input from "../../components/Input";
import PageHeader from "../../components/PageHeader";
import Pagination from "../../components/Pagination";
import Select from "../../components/Select";
import StatCard from "../../components/StatCard";
import Table from "../../components/Table";
import { api } from "../../services/api";

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: "",
    targetType: "",
    adminId: "",
  });
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [filters, pagination.page]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: "50",
      });
      if (filters.action) params.append("action", filters.action);
      if (filters.targetType) params.append("targetType", filters.targetType);
      if (filters.adminId) params.append("adminId", filters.adminId);

      const response = await api.get(`/admin/audit-logs?${params.toString()}`);
      setLogs(response.logs || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination.total,
        pages: response.pagination.pages,
      }));
    } catch (error) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get("/admin/audit-logs/stats");
      setStats(response.stats);
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getActionBadgeVariant = (action) => {
    if (action.includes("delete")) return "danger";
    if (action.includes("create")) return "success";
    if (
      action.includes("update") ||
      action.includes("ban") ||
      action.includes("unban")
    )
      return "warning";
    if (action.includes("bulk")) return "info";
    return "primary";
  };

  const actionOptions = [
    { value: "", label: "All Actions" },
    { value: "user_created", label: "User Created" },
    { value: "user_updated", label: "User Updated" },
    { value: "user_deleted", label: "User Deleted" },
    { value: "user_banned", label: "User Banned" },
    { value: "user_unbanned", label: "User Unbanned" },
    { value: "user_verified", label: "User Verified" },
    { value: "password_reset", label: "Password Reset" },
    { value: "subject_created", label: "Subject Created" },
    { value: "subject_updated", label: "Subject Updated" },
    { value: "subject_deleted", label: "Subject Deleted" },
    { value: "penalty_resolved", label: "Penalty Resolved" },
    { value: "bulk_action", label: "Bulk Action" },
  ];

  const targetTypeOptions = [
    { value: "", label: "All Types" },
    { value: "user", label: "User" },
    { value: "subject", label: "Subject" },
    { value: "penalty", label: "Penalty" },
    { value: "booking", label: "Booking" },
    { value: "session", label: "Session" },
    { value: "system", label: "System" },
  ];

  const columns = [
    {
      header: "Timestamp",
      accessor: "createdAt",
      sortable: true,
      render: (log) => formatDate(log.createdAt),
    },
    {
      header: "Admin",
      sortable: false,
      render: (log) => (
        <div>
          <div>
            {log.adminId?.name?.first} {log.adminId?.name?.last}
          </div>
          <div className="text-xs opacity-60">{log.adminId?.email}</div>
        </div>
      ),
    },
    {
      header: "Action",
      accessor: "action",
      sortable: true,
      render: (log) => (
        <Badge
          variant={getActionBadgeVariant(log.action) || "primary"}
          label={log.action.replace(/_/g, " ")}
          size="small"
          className="font-medium"
        />
      ),
    },
    {
      header: "Target",
      accessor: "targetType",
      sortable: true,
      render: (log) => (
        <div className="flex flex-col">
          <span className="font-medium">{log.targetType}</span>
          {log.targetId && (
            <span className="text-xs opacity-60 font-mono">
              {log.targetId.slice(-8)}
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Details",
      sortable: false,
      render: (log) => (
        <div className="max-w-xs">
          {log.details?.email && <div>Email: {log.details.email}</div>}
          {log.details?.name && (
            <div>
              Name: {log.details.name.first} {log.details.name.last}
            </div>
          )}
          {log.details?.role && <div>Role: {log.details.role}</div>}
          {log.details?.count && <div>Count: {log.details.count}</div>}
          {log.details?.action && <div>Action: {log.details.action}</div>}
        </div>
      ),
    },
    {
      header: "IP Address",
      accessor: "ipAddress",
      sortable: true,
      render: (log) => (
        <span className="opacity-70 font-mono">{log.ipAddress || "N/A"}</span>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Audit Logs"
        subtitle="Track all administrative actions performed on the platform"
      />

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard
            title="Total Actions"
            value={stats.total.toLocaleString()}
          />
          <StatCard
            title="Last 24 Hours"
            value={stats.last24Hours.toLocaleString()}
          />
          <Card padding="p-4">
            <h3 className="text-sm font-medium text-text opacity-70">
              Most Common Action
            </h3>
            <p className="text-lg font-semibold text-text">
              {stats.actionBreakdown[0]?._id.replace(/_/g, " ") || "N/A"}
            </p>
            <p className="text-sm text-text/70">
              {stats.actionBreakdown[0]?.count || 0} times
            </p>
          </Card>
        </div>
      )}

      <FilterPanel>
        <Select
          label="Action Type"
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          options={actionOptions}
        />
        <Select
          label="Target Type"
          value={filters.targetType}
          onChange={(e) =>
            setFilters({ ...filters, targetType: e.target.value })
          }
          options={targetTypeOptions}
        />
        <Input
          label="Admin"
          placeholder="Admin ID..."
          value={filters.adminId}
          onChange={(e) => setFilters({ ...filters, adminId: e.target.value })}
        />
      </FilterPanel>

      <Table
        columns={columns}
        data={logs.map((log) => ({ ...log, id: log._id }))}
        loading={loading}
        emptyMessage="No audit logs found"
      />

      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.pages}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
      />
    </div>
  );
}

export default AuditLogs;
